import { useMutation, useQuery, useQueryClient } from "react-query";
import http, { ResponseError } from "shared/http";
import { accessTokenName } from "shared/config";
import { getValueFromCookie } from "shared/helpers";

export interface Agency {
  code: string;
  tier: string;
}

export interface ResourceItem {
  resourceId: number;
  name: string;
  resourceType: string;
  summary?: string | null;
  url?: string | null;
}

export interface CategoryResource {
  categoryId: number;
  name: string;
  parentCategory: string | null;
  resources: ResourceItem[];
}

export interface User {
  userId: number;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  externalSystemId: string | null;
  payee?: string | null;
  authType: string | null;
  createdAt: string | null;
  lastLoginAt: string | null;
  roles: string[];
  agencies: Agency[];
  categoryResources: CategoryResource[];
}

export interface UserUpdateRequest {
  userId: number;
  firstname: string;
  lastname: string;
  email: string;
  isActive: boolean;
  externalSystemId: string | null;
  payee?: string | null;
  authType: string | null;
  password: string | null;
  roles: string[];
  agencies: string[];
}

export const getNameInitials = (user: User): string => {
  const firstInitial = user.firstname.charAt(0).toUpperCase() || "";
  const lastInitial = user.lastname.charAt(0).toUpperCase() || "";
  return firstInitial + lastInitial;
};

export const upsertUser = (user: UserUpdateRequest): Promise<User> => {
  if (user.userId > 0) {
    return http.putAsync(`admin/users/${user.userId}`, user);
  }
  return http.postAsync("admin/users", user);
};

export const useUpsertUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(upsertUser, {
    onSuccess: (savedUser) => {
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["users", savedUser.userId]);
    },
  });
};

const upsertAgencyManagerUser = (user: UserUpdateRequest): Promise<User> => {
  if (user.userId > 0) {
    return http.putAsync(`agencymanager/users/${user.userId}`, user);
  }
  return http.postAsync("agencymanager/users", user);
};

export const useAgencyManagerUpsertUserMutation = () => {
  return useMutation(upsertAgencyManagerUser);
};

const fetchUserById = async (userId: number): Promise<User | null> => {
  const data = await http.fetchAsync(`users/${userId}`);
  if (!data) return null;
  return data as User;
};

const fetchUserByEmail = async (email: string): Promise<User | null> => {
  const data = await http.fetchAsync(`users/by-email/${email}`);
  if (!data) return null;
  return data as User;
};

export const useGetUsersQuery = () => {
  return useQuery(
    ["users"],
    async (): Promise<User[] | null> => {
      return await http.fetchAsync("admin/users");
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

export const useUserByIdQuery = (userId: number) => {
  return useQuery(["users", userId], () => fetchUserById(userId), {
    enabled: userId != null,
  });
};

export const useAdminUserDetailQuery = (userId: number) => {
  return useQuery(["admin-user-detail", userId], () => fetchUserById(userId), {
    enabled: userId > 0,
    staleTime: 0,
  });
};

export const useCurrentUserQuery = () => {
  const hasToken = !!getValueFromCookie(accessTokenName, "token");

  return useQuery(
    ["currentUser"],
    async () => {
      const data = await http.fetchAsync(`users/current`);
      if (!data) return null;
      return data as User;
    },
    {
      enabled: hasToken,
      retry: (failureCount, error) => {
        if (error instanceof ResponseError) {
          const status = error.response?.status ?? error.status;
          if (status === 401 || status === 403) return false;
        }
        return failureCount < 3;
      },
    },
  );
};

export interface UserEmailAlert {
  email: string | null;
  isActive: boolean;
}

export const useUserEmailAlertQuery = () => {
  const hasToken = !!getValueFromCookie(accessTokenName, "token");

  return useQuery(
    ["userEmailAlert"],
    async (): Promise<UserEmailAlert | null> => {
      const data = await http.fetchAsync("users/email-alert");
      if (!data) return null;
      return data as UserEmailAlert;
    },
    { enabled: hasToken, staleTime: 5 * 60 * 1000 },
  );
};

export interface MyAgency {
  agency: string;
  agencyName: string;
  company: string;
  companyName: string;
}

export const useMyAgenciesQuery = () => {
  const hasToken = !!getValueFromCookie(accessTokenName, "token");

  return useQuery(
    ["myAgencies"],
    async (): Promise<MyAgency[]> => {
      try {
        const data = await http.fetchAsync("users/my-agencies");
        return (data ?? []).map((x: any) => ({
          agency: x.agency,
          agencyName: x.agencyName,
          company: x.company,
          companyName: x.companyName,
        }));
      } catch {
        return [];
      }
    },
    { enabled: hasToken, staleTime: 5 * 60 * 1000 },
  );
};

export interface UpdateMyProfileRequest {
  firstname: string;
  lastname: string;
  email: string;
}

export const useUpdateMyProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: UpdateMyProfileRequest) => http.putAsync("users/profile", payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["currentUser"]);
      },
    },
  );
};

export const useUpsertUserEmailAlertMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: UserEmailAlert) => http.putAsync("users/email-alert", payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["userEmailAlert"]);
      },
    },
  );
};
